import React, { useState, useEffect, useRef } from 'react';
import { Settings, ChevronDown } from 'lucide-react';
import MicSelector from '../DeviceSelector/MicSelector';
import CameraSelector from '../DeviceSelector/CameraSelector';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import styles from './DeviceSettingsPanel.module.css';

const DeviceSettingsPanel = ({
    selectedAudioDevice,
    selectedVideoDevice,
    selectedLanguage,
    onAudioDeviceChange,
    onVideoDeviceChange,
    onLanguageChange,
    disabled,
    isLiveMode,
    onMicToggle,
    onCameraToggle
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const [isMediumScreen, setIsMediumScreen] = useState(false);
    const panelRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        const checkScreenSize = () => {
            // Define medium screen breakpoint (e.g., 768px)
            setIsMediumScreen(window.matchMedia('(max-width: 768px)').matches);
        };

        // Initial check
        checkScreenSize();

        // Add resize listener
        window.addEventListener('resize', checkScreenSize);

        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen &&
                isMediumScreen && // Only close on click outside for medium screens
                panelRef.current &&
                buttonRef.current &&
                !panelRef.current.contains(event.target) &&
                !buttonRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, isMediumScreen]);

    return (
        <div className={styles.deviceSettingsWrapper}>
            <button
                ref={buttonRef}
                className={styles.settingsToggle}
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                disabled={disabled}
                aria-label="Toggle device settings"
            >
                <ChevronDown
                    size={18}
                    className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
                />
                <Settings size={18} />
            </button>

            <div
                ref={panelRef}
                className={`${styles.deviceSelectors} ${isOpen ? styles.open : ''}`}
            >
                <MicSelector
                    selectedAudioDevice={selectedAudioDevice}
                    onAudioDeviceChange={onAudioDeviceChange}
                    disabled={disabled}
                    onMicToggle={onMicToggle}
                />
                <CameraSelector
                    selectedVideoDevice={selectedVideoDevice}
                    onVideoDeviceChange={onVideoDeviceChange}
                    disabled={disabled}
                    onCameraToggle={onCameraToggle}
                />
                <LanguageSelector
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={onLanguageChange}
                    disabled={disabled}
                    isLiveMode={isLiveMode}
                />
            </div>
        </div>
    );
};

export default DeviceSettingsPanel;